import React, { useEffect, useRef, useState } from 'react'
import { Grid, Step, StepLabel, Stepper, Typography } from '@mui/material';
import AccountTypeInformation from './AccountTypeInformation';
import BeneficiaryInformation from './BeneficiaryInformation';
import TransferInformation from './TransferInformation';
import BankInformation from './BankInformation';
import EftInformation from './EftInformation';
import PacInformation from './PacInformation';
import AccountReview from './AccountReview';
import axiosInstance from 'custom-axios';
import SwpInformation from './SwpInformation';
import IncomeSweep from './IncomeSweep';
import { makeStyles } from "@material-ui/core/styles";
import { toast, ToastContainer } from 'react-toastify';
import { useFormContext } from 'react-hook-form';

const useStyles = makeStyles((theme) => ({
    inactiveStep: {
        color: 'grey !important',
        cursor: 'not-allowed',
    },
    activeStep: {
        color: 'black',
    },
    stepIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: 'lightgray',
        color: 'black',
    },
    activeStepIcon: {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
    },
    optionalLabel: {
        display: 'block',
        fontSize: '0.8em',
        color: 'grey',
    },
}));

const AccountStepper = ({ parentErrorStepIndex, setParentErrorStepIndex, userId, setActiveChildStep, activeChildStep, formAccountData, setFormAccountData, parentStep, setActiveParentStep }) => {

    const classes = useStyles();
    let loginId = localStorage.getItem('login_id');
    const loginType = localStorage.getItem('login_type');
    let parentActiveAccountTypeId = 1;

    let accountStep = ['Account Type & Information', 'Beneficiary Information', 'Transfer Details', 'Bank Information', 'EFT Details', 'PAC Details', 'SWP Details', 'Income Sweep']
    const [accountSteps, setAccountSteps] = useState(accountStep);
    const [accountPacData, setAccountPacData] = useState([]);
    const [accountSwpData, setAccountSwpData] = useState([]);
    const [accountIncomeSweepData, setAccountSweepIncomeData] = useState([]);
    const [activeAccountStep, setActiveAccountStep] = useState(activeChildStep);
    const [accountStepError, setAccountStepError] = React.useState([false, false, false, false, false, false, false, false, false]); // Track errors for each step
    // const [liveAccountParentErrorStep, setLiveAccountParentErrorStep] = useState(parentErrorStepIndex)

    // console.log(formAccountData.beneficiary_details)
    const { getValues } = useFormContext();

    const accountStep0Ref = useRef();
    const accountStep1Ref = useRef();
    const accountStep2Ref = useRef();
    const accountStep5Ref = useRef();
    const accountStep6Ref = useRef();
    const accountStep7Ref = useRef();

    const stepRefs = [
        accountStep0Ref, // Account Type & Information
        accountStep1Ref, // Beneficiary Information
        accountStep2Ref, // Transfer Details
        undefined,       // Bank Information (No validation required)
        undefined, // EFT Details (No validation required)
        accountStep5Ref,       // PAC Details 
        accountStep6Ref,       // SWP Details
        accountStep7Ref,       // Income Sweep
        undefined        // Account Review Screen (No validation required)
    ];


    const clickOnAccountChildStep = async (index) => {

        const stepEndpoints = [
            'api/save-user-account-type-info',
            'api/save-user-beneficiary-info',
            'api/save-user-transfer-info',
            'api/save-user-bank-info',
            'api/save-user-etf-info',
            'api/save-user-pac-info',
            'api/save-user-swp-info',
            'api/save-user-income-sweep-info',
            'api/change-form-step'
        ];


        let isValid = true;
        let submitData;
        const endPoint = stepEndpoints[activeAccountStep];

        // Perform validation for steps with refs
        if (stepRefs[activeAccountStep]) {

            isValid = await stepRefs[activeAccountStep]?.current?.triggerValidation();
        }

        let updatedErrors = [...accountStepError];
        updatedErrors[activeAccountStep] = !isValid;

        setAccountStepError(updatedErrors)

        const hasTrueValue1 = updatedErrors.some(value => value === true);

        const updatedErrorParentState = [...parentErrorStepIndex];

        if (hasTrueValue1) {
            updatedErrorParentState[1] = true; // Update index 1
        } else {
            updatedErrorParentState[1] = false; // Reset index 1
        }


        if (endPoint === 'api/change-form-step') {
            // Submit without formData for the 'change-form-step' endpoint
            submitData = {
                user_id: userId || 0,
                parent_step: 2,
                child_step: index,
                child_account_error_step: updatedErrors,
                parent_error_step: updatedErrorParentState
            };

        } else if (endPoint === 'api/save-user-transfer-info') {

            const formTransferData = new FormData();

            formTransferData.append('login_id', formAccountData.login_id || 0);
            formTransferData.append('accountTypeId', parentActiveAccountTypeId || '');
            formTransferData.append('user_id', userId || 0);
            formTransferData.append('parent_step', 1);
            formTransferData.append('child_step', index);
            formTransferData.append('child_account_error_step', updatedErrors);
            formTransferData.append('parent_error_step', updatedErrorParentState);


            const currentTransferDetails = getValues('transfer_details') || formAccountData.transfer_details;
            currentTransferDetails.forEach((transferDetail, transferIndex) => {
                formTransferData.append(`add_transfer_details[${transferIndex}][id]`, transferDetail.id || '');
                formTransferData.append(`add_transfer_details[${transferIndex}][irrevocable_information]`, transferDetail.irrevocable_information || '');
                formTransferData.append(`add_transfer_details[${transferIndex}][street_number]`, transferDetail.street_number || '');
                formTransferData.append(`add_transfer_details[${transferIndex}][street_name]`, transferDetail.street_name || '');
                formTransferData.append(`add_transfer_details[${transferIndex}][street_type]`, transferDetail.street_type || '');
                formTransferData.append(`add_transfer_details[${transferIndex}][city]`, transferDetail.city || '');
                formTransferData.append(`add_transfer_details[${transferIndex}][province]`, transferDetail.province || '');
                formTransferData.append(`add_transfer_details[${transferIndex}][country]`, transferDetail.country || '');
                formTransferData.append(`add_transfer_details[${transferIndex}][postal_code]`, transferDetail.postal_code || '');
                formTransferData.append(`add_transfer_details[${transferIndex}][business_phone]`, transferDetail.business_phone || '');
                formTransferData.append(`add_transfer_details[${transferIndex}][extension]`, transferDetail.extension || '');
                formTransferData.append(`add_transfer_details[${transferIndex}][institution_contact]`, transferDetail.institution_contact || '');
                formTransferData.append(`add_transfer_details[${transferIndex}][dealer_number]`, transferDetail.dealer_number || '');

                transferDetail.transfer_instructions.forEach((instruction, instructionIndex) => {
                    formTransferData.append(`add_transfer_details[${transferIndex}][transfer_instructions][${instructionIndex}][id]`, instruction.id || '');
                    formTransferData.append(`add_transfer_details[${transferIndex}][transfer_instructions][${instructionIndex}][instruction_currency]`, instruction.instruction_currency || '');
                    formTransferData.append(`add_transfer_details[${transferIndex}][transfer_instructions][${instructionIndex}][institution_account_number]`, instruction.institution_account_number || '');
                    formTransferData.append(`add_transfer_details[${transferIndex}][transfer_instructions][${instructionIndex}][institution_transfer_type]`, instruction.institution_transfer_type || '');
                    formTransferData.append(`add_transfer_details[${transferIndex}][transfer_instructions][${instructionIndex}][ti_file]`, instruction.ti_file || '');
                });
            });

            submitData = formTransferData

        } else if (endPoint === 'api/save-user-bank-info') {

            const formBankData = new FormData();
            formBankData.append('login_id', formAccountData.login_id || 0);
            formBankData.append('accountTypeId', parentActiveAccountTypeId || '');
            formBankData.append('user_id', userId || 0);
            formBankData.append('parent_step', 1);
            formBankData.append('child_step', index);
            formBankData.append('child_account_error_step', updatedErrors);
            formBankData.append('parent_error_step', updatedErrorParentState);

            const currentBankDetails = getValues('bank_information') || formAccountData.bank_information;
            currentBankDetails.forEach((bankDetail, index) => {
                formBankData.append(`add_bank_details[${index}][id]`, bankDetail.id || '');
                formBankData.append(`add_bank_details[${index}][user_id]`, bankDetail.user_id || '');
                formBankData.append(`add_bank_details[${index}][account_type_id]`, bankDetail.account_type_id || '');
                formBankData.append(`add_bank_details[${index}][bank_name]`, bankDetail.bank_name || '');
                formBankData.append(`add_bank_details[${index}][bank_transit_number]`, bankDetail.bank_transit_number || '');
                formBankData.append(`add_bank_details[${index}][bank_account_number]`, bankDetail.bank_account_number || '');
                formBankData.append(`add_bank_details[${index}][bank_currency]`, bankDetail.bank_currency || '');
                formBankData.append(`add_bank_details[${index}][bank_institution_number]`, bankDetail.bank_institution_number || '');


            });
            submitData = formBankData


        } else if (endPoint === 'api/save-user-etf-info') {

            const currentEftInfo = getValues('eft_information') || formAccountData.eft_information;
            const currentEftFiltered = getValues('filtered_account_details') || [];

            submitData = {
                ...(currentEftInfo && currentEftInfo[0]),
                ...(currentEftFiltered && currentEftFiltered[0]),
                login_id: formAccountData.login_id || 0,
                accountTypeId: parentActiveAccountTypeId || '',
                user_id: userId || 0,
                parent_step: 1,
                child_step: index,
                child_account_error_step: updatedErrors,
                parent_error_step: updatedErrorParentState
            };

        } else if (endPoint === 'api/save-user-pac-info') {

            const bankingPacData1 = {
                bank_account_number: accountPacData.bank_account_number,
                bank_institution_number: accountPacData.bank_institution_number,
                bank_name: accountPacData.bank_name,
                bank_transit_number: accountPacData.bank_transit_number
            }

            submitData = {
                ...accountPacData[0],
                ...bankingPacData1,
                ...getValues(),
                login_id: formAccountData.login_id || 0,
                accountTypeId: parentActiveAccountTypeId || '',
                user_id: userId || 0,
                parent_step: 1,
                child_step: index,
                child_account_error_step: updatedErrors,
                parent_error_step: updatedErrorParentState
            };
            // return;
        } else if (endPoint === 'api/save-user-swp-info') {

            const bankingSwpData1 = {
                bank_account_number: accountSwpData.bank_account_number,
                bank_institution_number: accountSwpData.bank_institution_number,
                bank_name: accountSwpData.bank_name,
                bank_transit_number: accountSwpData.bank_transit_number
            }

            submitData = {
                ...accountSwpData[0],
                ...bankingSwpData1,
                ...getValues(),
                login_id: formAccountData.login_id || 0,
                accountTypeId: parentActiveAccountTypeId || '',
                user_id: userId || 0,
                parent_step: 1,
                child_step: index,
                child_account_error_step: updatedErrors,
                parent_error_step: updatedErrorParentState
            };

        } else if (endPoint === 'api/save-user-income-sweep-info') {

            const currentIncomeSweepData = getValues('income_sweep_details') || accountIncomeSweepData;

            const bankingIncomeSweepData1 = {
                bank_account_number: currentIncomeSweepData[0]?.bank_account_number || accountIncomeSweepData.bank_account_number,
                bank_institution_number: currentIncomeSweepData[0]?.bank_institution_number || accountIncomeSweepData.bank_institution_number,
                bank_name: currentIncomeSweepData[0]?.bank_name || accountIncomeSweepData.bank_name,
                bank_transit_number: currentIncomeSweepData[0]?.bank_transit_number || accountIncomeSweepData.bank_transit_number
            }

            submitData = {
                ...currentIncomeSweepData[0],
                ...bankingIncomeSweepData1,
                ...getValues(),
                login_id: formAccountData.login_id || 0,
                accountTypeId: parentActiveAccountTypeId || '',
                user_id: userId || 0,
                parent_step: 1,
                child_step: index,
                child_account_error_step: updatedErrors,
                parent_error_step: updatedErrorParentState
            };

        } else if (endPoint === 'api/save-user-beneficiary-info') {

            submitData = {
                ...formAccountData,
                ...getValues(),
                login_id: formAccountData.login_id,
                accountTypeId: parentActiveAccountTypeId,
                user_id: userId ? userId : 0,
                parent_step: 1,
                child_step: index,
                child_account_error_step: updatedErrors,
                parent_error_step: updatedErrorParentState
            }

        } else {

            submitData = {
                ...formAccountData,
                ...getValues(),
                login_id: formAccountData.login_id,
                accountTypeId: parentActiveAccountTypeId,
                user_id: userId ? userId : 0,
                parent_step: 1,
                child_step: index,
                child_account_error_step: updatedErrors,
                parent_error_step: updatedErrorParentState
            }
        }


        setActiveAccountStep(index)

        try {
            const response = await axiosInstance.post(endPoint, submitData);

            if (response.status == 500) {
                toast.error(response.message)
            }

        } catch (error) {
            // console.error(error)
        }
    }

    const fetchAccountTypeInfoData = async () => {
        try {
            const response = await axiosInstance.get(`api/get-user-account-type-info/${userId}`);

            setActiveParentStep(1)

            if (response.data) {

                setFormAccountData(prevFormData => ({
                    ...prevFormData,
                    account_transfer: response.data.account_transfer,
                    account_eft: response.data.account_eft,
                    account_pac: response.data.account_pac,
                    account_swp: response.data.account_swp,
                    account_income_sweep: response.data.account_income_sweep,
                }));
            }
        } catch (error) {
            // console.error(error.message);
        }
    }

    const fetchAccountFormStep = async () => {

        try {

            const response = await axiosInstance.get(`api/fetch-form-step/${userId}`);

            if (response.data) {

                const ChildErrorStepArray = response.data?.child_account_error_step
                    .split(",") // Split the string into an array
                    .map((value) => value === "1"); // Convert "1" to true and "0" to false

                setAccountStepError(ChildErrorStepArray)
            }

        } catch (error) {
            // console.error(error.message);
        }
    };

    const getHouseholdSteps = (progress) => {
        switch (progress) {
            case 0:
                return <AccountTypeInformation accountTypeRef={accountStep0Ref} setActiveChildStep={setActiveChildStep} accountTypeId={1} userId={userId} formAccountData={formAccountData} setFormAccountData={setFormAccountData} activeAccountStep={activeAccountStep} setActiveAccountStep={setActiveAccountStep} setActiveParentStep={setActiveParentStep} />;
            case 1:
                return <BeneficiaryInformation beneficiaryRef={accountStep1Ref} accountTypeId={1} userId={userId} formAccountData={formAccountData} setFormAccountData={setFormAccountData} activeAccountStep={activeAccountStep} setActiveAccountStep={setActiveAccountStep} />;
            case 2:
                return <TransferInformation transferInfoRef={accountStep2Ref} accountTypeId={1} userId={userId} formAccountData={formAccountData} setFormAccountData={setFormAccountData} activeAccountStep={activeAccountStep} setActiveAccountStep={setActiveAccountStep} />;
            case 3:
                return <BankInformation accountTypeId={1} userId={userId} formAccountData={formAccountData} setFormAccountData={setFormAccountData} activeAccountStep={activeAccountStep} setActiveAccountStep={setActiveAccountStep} />;
            case 4:
                return <EftInformation accountTypeId={1} userId={userId} formAccountData={formAccountData} activeAccountStep={activeAccountStep} setFormAccountData={setFormAccountData} setActiveAccountStep={setActiveAccountStep} />;
            case 5:
                return <PacInformation pacRef={accountStep5Ref} setAccountPacData={setAccountPacData} accountTypeId={1} userId={userId} formAccountData={formAccountData} activeAccountStep={activeAccountStep} setFormAccountData={setFormAccountData} setActiveAccountStep={setActiveAccountStep} parentStep={parentStep} setActiveParentStep={setActiveParentStep} />;
            case 6:
                return <SwpInformation swpRef={accountStep6Ref} setAccountSwpData={setAccountSwpData} accountTypeId={1} userId={userId} formAccountData={formAccountData} activeAccountStep={activeAccountStep} setFormAccountData={setFormAccountData} setActiveParentStep={setActiveParentStep} setActiveAccountStep={setActiveAccountStep} />;
            case 7:
                return <IncomeSweep incomeRef={accountStep7Ref} setAccountSweepIncomeData={setAccountSweepIncomeData} accountTypeId={1} userId={userId} formAccountData={formAccountData} activeAccountStep={activeAccountStep} setFormAccountData={setFormAccountData} setActiveParentStep={setActiveParentStep} setActiveAccountStep={setActiveAccountStep} />;
            // case 8:
            //     return <AccountReview userId={userId} accountStepError={accountStepError} activeAccountStep={activeAccountStep} setActiveAccountStep={setActiveAccountStep} parentStep={parentStep} setActiveParentStep={setActiveParentStep}  login_id={formAccountData.login_id} />;
            default:
                break;
        }
    }

    const isStepDisabled = (label) => {
        return label.includes('(N/A)');
    };


    useEffect(() => {

        const hasTrueValue = accountStepError.some(value => value === true);

        setParentErrorStepIndex((prevState) => {
            const updatedState = [...prevState];

            if (hasTrueValue) {
                updatedState[1] = true; // Update index 1
            } else {
                updatedState[1] = false; // Reset index 1
            }

            return updatedState;
        });

    }, [accountStepError, setParentErrorStepIndex]); // Run this effect whenever accountStepError changes


    useEffect(() => {
        fetchAccountTypeInfoData()
        fetchAccountFormStep()

        setFormAccountData({
            intended_use_of_account: [],
            login_id: loginId,
            beneficiary_details: {
                elect_account_beneficiary: '',
                elect_contingent_beneficiary: '',

                elect_account_beneficiary_details: [
                    {
                        primary: {},
                        spouse: {},
                        details: { id: 0, first_name: "", last_name: "", middle_name: "", sin: '', dob: '', allocation: '', relation: '' }
                    }
                ],

                elect_contingent_beneficiary_details: [
                    { id: 0, first_name: "", last_name: "", middle_name: "", sin: '', dob: '', allocation: '', relation: '' }
                ]
            },

            transfer_details: [{
                id: '',
                irrevocable_information: '',
                country: '',
                street_number: '',
                street_name: '',
                street_type: '',
                city: '',
                province: '',
                postal_code: '',
                business_phone: '',
                extension: '',
                dealer_number: '',
                institution_contact: '',
                transfer_instructions: [
                    { instruction_currency: "", institution_account_number: "", institution_transfer_type: "", ti_file: '' }
                ]
            }],

            bank_information: [{
                bank_name: '',
                bank_currency: '',
                bank_transit_number: '',
                bank_institution_number: '',
                bank_account_number: '',
            }],

            pac_details: [{
                pac_id: '',
                pac_frequency: '',
                pac_account_option: '',
                pac_investment_account_details: [],
                pac_investment_account_detail_id: 0,
                pac_bank_account_detail_id: 0,
                pac_start_date: '',
                pac_account_number: '',
                pac_bank_name: '',
                pac_transit_number: '',
                pac_institution_number: '',
                plan_investment_instruction: [{
                    id: '',
                    pii_contribution_type: '',
                    pii_fund_number: '',
                    pii_fund_name: '',
                    pii_gross_amount: ''
                }],
            }],

            swp_details: [{
                swp_id: '',
                swp_frequency: '',
                swp_account_option: '',
                swp_investment_account_details: [],
                swp_investment_account_detail_id: 0,
                swp_bank_account_detail_id: 0,
                swp_start_date: '',
                swp_account_number: '',
                swp_bank_name: '',
                swp_transit_number: '',
                swp_institution_number: '',
                swp_plan_investment_instruction: [{
                    id: '',
                    swp_pii_contribution_type: '',
                    swp_pii_fund_number: '',
                    swp_pii_fund_name: '',
                    swp_pii_gross_amount: ''
                }],
            }],

            income_sweep_details: [{
                income_sweep_id: '',
                income_sweep_frequency: '',
                income_sweep_account_option: '',
                income_sweep_investment_account_details: [],
                income_sweep_investment_account_detail_id: 0,
                income_sweep_bank_account_detail_id: 0,
                income_sweep_start_date: '',
                income_sweep_account_number: '',
                income_sweep_bank_name: '',
                income_sweep_transit_number: '',
                income_sweep_institution_number: '',

                income_sweep_plan_investment_instruction: [{
                    id: '',
                    income_sweep_pii_contribution_type: '',
                    income_sweep_pii_fund_number: '',
                    income_sweep_pii_fund_name: '',
                    income_sweep_pii_gross_amount: ''
                }],
            }],
        });

    }, [])



    useEffect(() => {

        let steps = [
            'Account Type & Information',
            'Beneficiary Information',
            formAccountData.account_transfer === 2 ? 'Transfer Details (N/A)' : 'Transfer Details',
            'Bank Information',
            formAccountData.account_eft === 2 ? 'EFT Details (N/A)' : 'EFT Details',
            formAccountData.account_pac === 2 ? 'PAC Details (N/A)' : 'PAC Details',
            formAccountData.account_swp === 2 ? 'SWP Details (N/A)' : 'SWP Details',
            // formAccountData.account_income_sweep === 2 ? 'Income Sweep (N/A)' : 'Income Sweep',
            'Income Sweep (N/A)',
            // 'Account Review Screen', // Optionally include a review screen
        ];

        setAccountSteps(steps);
    }, [formAccountData, loginType]);


    return (
        <>
            {activeAccountStep !== accountSteps.length ? (
                <>
                    <div className='client-form-box'>

                        <div className='border-solid-ced4da'>

                            <Grid item xs={12} sm={6} className='pt-3'>

                                <Stepper activeStep={activeAccountStep} alternativeLabel>

                                    {accountSteps.map((label, index) => {
                                        const labelProps1 = {};

                                        if (accountStepError[index]) {
                                            labelProps1.optional = (
                                                <Typography variant="caption" color="error"></Typography>
                                            );

                                            labelProps1.error = true;
                                        }
                                        return (
                                            <Step key={label}>
                                                <StepLabel
                                                    {...labelProps1}
                                                    onClick={() => clickOnAccountChildStep(index)}
                                                    StepIconProps={{
                                                        classes: {
                                                            root: isStepDisabled(label) ? classes.inactiveStep : '',
                                                        },
                                                    }}

                                                    className={`${isStepDisabled(label) ? classes.inactiveStep : ''} cursor-pointer`}


                                                >{label}</StepLabel>

                                            </Step>
                                        )
                                    })}
                                </Stepper>
                            </Grid>

                            {getHouseholdSteps(activeAccountStep)}

                        </div>
                    </div>
                </>
            ) : (
                null
            )}
            <ToastContainer />
        </>
    )
}

export default AccountStepper